import React, { useState, useCallback, useEffect, useContext } from "react";
import { Fab, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Typography, Box, Checkbox, Button } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import LoadingButton from '@mui/lab/LoadingButton';
import PrivateContext from "../context/PrivateContext";
import jsAPI from "../helpers/jsAPI";
import RichTextEditor from "./RichTextEditor";

const RedAlertButton = () => {
  const privateContext = useContext(PrivateContext);
  const { isClient, sessionSocket } = privateContext;
  
  const [redAlertEligible, setRedAlertEligible] = useState(false);
  const [redAlertEligibilityReason, setRedAlertEligibilityReason] = useState("");
  const [redAlertEnabled, setRedAlertEnabled] = useState(false);
  const [openRedAlertModal, setOpenRedAlertModal] = useState(false);
  const [redAlertConcern, setRedAlertConcern] = useState("");
  const [redAlertDetails, setRedAlertDetails] = useState("");
  const [redAlertSelectedTickets, setRedAlertSelectedTickets] = useState([]);
  const [redAlertUnresolvedTickets, setRedAlertUnresolvedTickets] = useState([]);
  const [redAlertSubmitting, setRedAlertSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadRedAlertEligibility = useCallback(async () => {
    if (!isClient) return;
    try {
      const responseJSON = await jsAPI.request('/api/red_alerts/eligibility');
      setRedAlertEnabled(responseJSON.red_alert_enabled ?? responseJSON.redAlertEnabled ?? false);
      setRedAlertEligible(responseJSON.eligible || false);
      setRedAlertEligibilityReason(responseJSON.reason || "");
    } catch (err) {
      setRedAlertEnabled(false);
      setRedAlertEligible(false);
      setRedAlertEligibilityReason("Unable to check eligibility.");
      console.error('Error loading Red Alert eligibility:', err);
    }
  }, [isClient]);

  const loadUnresolvedTickets = useCallback(async () => {
    if (!isClient) return;
    try {
      const responseJSON = await jsAPI.request('/api/red_alerts/unresolved_tickets');
      setRedAlertUnresolvedTickets(responseJSON.data || []);
    } catch (err) {
      setRedAlertUnresolvedTickets([]);
      console.error('Error loading unresolved tickets:', err);
    }
  }, [isClient]);

  const loadActiveRedAlert = useCallback(async () => {
    if (!isClient) return;
    try {
      const responseJSON = await jsAPI.request('/api/red_alerts/active');
      if (responseJSON.data) {
        // If there's an active Red Alert, user is not eligible
        setRedAlertEligible(false);
        setRedAlertEligibilityReason('You already have an active Red Alert.');
      }
    } catch (err) {
      console.error('Error loading active Red Alert:', err);
    }
  }, [isClient]);

  useEffect(() => {
    if (isClient) {
      loadRedAlertEligibility();
      loadActiveRedAlert();
    }
  }, [isClient, loadRedAlertEligibility, loadActiveRedAlert]);

  // Listen for Red Alert updates (for clients)
  useEffect(() => {
    if (isClient) {
      const handler = () => {
        loadRedAlertEligibility();
        loadActiveRedAlert();
      };

      sessionSocket?.on("red-alert-created-client", handler);
      return () => sessionSocket?.off("red-alert-created-client", handler);
    }
  }, [isClient, sessionSocket, loadRedAlertEligibility, loadActiveRedAlert]);

  const handleRedAlertSubmit = async () => {
    if (!redAlertConcern.trim()) {
      setErrorMessage("Concern is required.");
      return;
    }
    
    const detailsText = redAlertDetails.replace(/<[^>]*>/g, '').trim();
    if (!detailsText) {
      setErrorMessage("Details are required.");
      return;
    }

    setRedAlertSubmitting(true);
    setErrorMessage("");
    try {
      const responseJSON = await jsAPI.request('/api/red_alerts', {
        method: 'POST',
        body: JSON.stringify({
          concern: redAlertConcern,
          details: redAlertDetails,
          ticket_ids: redAlertSelectedTickets
        })
      });

      if (responseJSON.success) {
        setSuccessMessage("Red Alert created successfully. A Senior Department Head will be notified immediately.");
        setOpenRedAlertModal(false);
        setRedAlertConcern("");
        setRedAlertDetails("");
        setRedAlertSelectedTickets([]);
        loadRedAlertEligibility();
        loadActiveRedAlert();
        
        // Emit socket event for Red Alert notification
        if (sessionSocket && responseJSON.data && responseJSON.data.id) {
          sessionSocket.emit('red-alert-created', {
            red_alert_id: responseJSON.data.id,
            client_id: responseJSON.data.client_id,
            client_name: responseJSON.data.client_name,
            concern: redAlertConcern
          });
        }
      } else {
        setErrorMessage(responseJSON.error_message || "Failed to create Red Alert.");
      }
    } catch (err) {
      setErrorMessage(err.message || "Failed to create Red Alert.");
    } finally {
      setRedAlertSubmitting(false);
    }
  };

  const handleRedAlertModalOpen = () => {
    setOpenRedAlertModal(true);
    setErrorMessage("");
    setSuccessMessage("");
    loadUnresolvedTickets();
  };

  const handleRedAlertModalClose = () => {
    setOpenRedAlertModal(false);
    setRedAlertConcern("");
    setRedAlertDetails("");
    setRedAlertSelectedTickets([]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  // Show success/error messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  if (!isClient || !redAlertEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating Red Alert Button */}
      <Tooltip 
        title={redAlertEligible ? "Raise Red Alert" : redAlertEligibilityReason || "Raise Red Alert"} 
        arrow
      >
        <Fab
          color="error"
          aria-label="raise red alert"
          onClick={handleRedAlertModalOpen}
          disabled={!redAlertEligible}
          sx={{
            position: "fixed",
            bottom: 10,
            left: 60,
            zIndex: 1000,
            backgroundColor: redAlertEligible ? "#ff0000" : "#cccccc",
            "&:hover": {
              backgroundColor: redAlertEligible ? "#cc0000" : "#aaaaaa",
            },
            "&.Mui-disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          <WarningIcon />
        </Fab>
      </Tooltip>

      {/* Red Alert Modal */}
      <Dialog
        open={openRedAlertModal}
        onClose={handleRedAlertModalClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#ff0000", color: "#ffffff", fontWeight: "bold" }}>
          🚨 Red Alert - Emergency Escalation
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
              {successMessage}
            </Typography>
          )}
          
          <DialogContentText sx={{ mb: 3, color: "#d32f2f", fontWeight: "bold" }}>
            This is an emergency escalation. A Senior Department Head will be notified immediately.
          </DialogContentText>
          
          <TextField
            fullWidth
            label="Concern *"
            value={redAlertConcern}
            onChange={(e) => setRedAlertConcern(e.target.value)}
            placeholder="Brief description of your concern"
            required
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 500 }}
          />

          <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
            Details *
          </Typography>
          <RichTextEditor
            value={redAlertDetails}
            onChange={setRedAlertDetails}
            viewMode={false}
          />

          {redAlertUnresolvedTickets.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
                Link Existing Tickets (Optional)
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: "250px", 
                  overflowY: "auto", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: 2, 
                  p: 1.5,
                  backgroundColor: "#fafafa"
                }}
              >
                {redAlertUnresolvedTickets.map((ticket) => (
                  <Box
                    key={ticket.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      backgroundColor: redAlertSelectedTickets.includes(ticket.id) ? "#fff3cd" : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: redAlertSelectedTickets.includes(ticket.id) ? "#ffe69c" : "#f5f5f5",
                        borderColor: "#1976d2",
                      },
                    }}
                    onClick={() => {
                      if (redAlertSelectedTickets.includes(ticket.id)) {
                        setRedAlertSelectedTickets(redAlertSelectedTickets.filter(id => id !== ticket.id));
                      } else {
                        setRedAlertSelectedTickets([...redAlertSelectedTickets, ticket.id]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={redAlertSelectedTickets.includes(ticket.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setRedAlertSelectedTickets([...redAlertSelectedTickets, ticket.id]);
                        } else {
                          setRedAlertSelectedTickets(redAlertSelectedTickets.filter(id => id !== ticket.id));
                        }
                      }}
                      sx={{ mt: -0.5, mr: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {ticket.number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {ticket.subject || "No subject"}
                      </Typography>
                      {ticket.status && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Status: {ticket.status}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
                💡 Ticket selection is optional. You can create a Red Alert without linking tickets.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleRedAlertModalClose} variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleRedAlertSubmit}
            variant="contained"
            loading={redAlertSubmitting}
            disabled={!redAlertConcern.trim() || !redAlertDetails || redAlertDetails.replace(/<[^>]*>/g, '').trim() === ''}
            sx={{
              backgroundColor: "#ff0000",
              "&:hover": {
                backgroundColor: "#cc0000",
              },
            }}
          >
            Submit Red Alert
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RedAlertButton;

